import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type GeocodeStatus = 'idle' | 'success' | 'not_found' | 'invalid' | 'error';

interface GeocodeResult {
  lat: number;
  lng: number;
  displayName: string;
}

export interface GeocodeOutcome {
  status: GeocodeStatus;
  result: GeocodeResult | null;
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

export function useGeocoding() {
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeResult, setGeocodeResult] = useState<string | null>(null);
  const [geocodeStatus, setGeocodeStatus] = useState<GeocodeStatus>('idle');

  const geocode = useCallback(async (params: {
    address: string;
    city?: string;
    region?: string;
  }): Promise<GeocodeResult | null> => {
    if (!params.address || params.address.trim().length < 4) {
      setGeocodeStatus('not_found');
      return null;
    }

    setIsGeocoding(true);
    setGeocodeResult(null);
    setGeocodeStatus('idle');

    try {
      const response = await supabase.functions.invoke('geocode-address', {
        body: {
          provider: 'nominatim',
          address: params.address,
          city: params.city || undefined,
          region: params.region || undefined,
          country: 'Sweden',
        },
      });

      if (response.error) {
        console.warn('[geocoding] Edge function error:', response.error.message);
        setGeocodeStatus('error');
        return null;
      }

      const data = response.data;
      

      if (!data || data.error) {
        if (data?.error === 'not_found') {
          setGeocodeStatus('not_found');
        } else {
          console.warn('[geocoding] Service error:', data?.error);
          setGeocodeStatus('error');
        }
        return null;
      }

      const first = data?.items?.[0];
      const lat = first?.lat;
      const lng = first?.lng;

      if (!first) {
        setGeocodeStatus('not_found');
        return null;
      }

      if (!isFiniteNumber(lat) || !isFiniteNumber(lng)) {
        console.warn('[geocoding] Invalid coordinates in response:', { lat, lng, first });
        setGeocodeStatus('invalid');
        return null;
      }

      setGeocodeResult(first.label ?? `${lat.toFixed(5)}, ${lng.toFixed(5)}`);
      setGeocodeStatus('success');

      return {
        lat,
        lng,
        displayName: first.label ?? '',
      };
    } catch (error) {
      console.warn('[geocoding] Unexpected error:', error);
      setGeocodeStatus('error');
      return null;
    } finally {
      setIsGeocoding(false);
    }
  }, []);

  return {
    geocode,
    isGeocoding,
    geocodeResult,
    geocodeStatus,
  };
}